import { useState, useEffect } from "react";
import { 
  Cpu, RefreshCw, Play, Wallet, Users, ArrowUpRight, ArrowDownRight, Clock, History, 
  PieChart, X, Check, Minus, Target, TrendingUp, ChevronUp, ChevronDown,
  ShieldAlert, Crosshair, Bell, Trophy, Star, BarChart3, UserCheck
} from "lucide-react";
import { StockSearch } from "./StockSearch";
import { formatNumber, getTickerCurrency } from "../utils/trading";
import { finnhubAPI } from "../services/api";
import FundamentalsProPanel from "./FundamentalsProPanel";
import {
  Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer,
  ReferenceLine, Bar, ComposedChart, Cell, BarChart as RechartsBarChart, Line, LineChart
} from "recharts";

function ScoreGauge({ score, size = 80 }) {
  const radius = (size - 10) / 2;
  const circumference = radius * Math.PI;
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? "#22c55e" : score >= 40 ? "#eab308" : "#ef4444";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size / 2 + 10 }}>
      <svg width={size} height={size / 2 + 10}>
        <path d={`M 5 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2}`} fill="none" stroke="#27272a" strokeWidth="8" strokeLinecap="round" />
        <path d={`M 5 ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2}`} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${progress} ${circumference}`} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
      </svg>
      <div className="absolute bottom-0 text-center"><span className="font-mono text-2xl font-bold" style={{ color }}>{score}</span></div>
    </div>
  );
}

const SignalBadge = ({ signal }) => {
  const styles = {
    "STRONG_BUY": "bg-cyan-500/25 text-cyan-300 border-cyan-500/40",
    "BUY": "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
    "HOLD": "bg-blue-500/20 text-blue-400 border-blue-500/30",
    "SELL": "bg-red-500/20 text-red-400 border-red-500/30",
    "STRONG_SELL": "bg-red-500/30 text-red-300 border-red-500/50",
  };
  return (
    <span className={`px-3 py-1 rounded text-xs font-bold uppercase border ${styles[signal] || "bg-slate-700 text-slate-300 border-slate-600"}`}>
      {signal?.replace("_", " ")}
    </span>
  );
};

function calcPositionSize(portfolioValue, analysis) {
  if (!portfolioValue || portfolioValue <= 0) return null;
  const ts = analysis?.trade_setup;
  const ind = analysis?.indicators;
  const an = analysis?.analysis;
  const si = analysis?.sector_info;
  if (!ts?.entry || ts.entry <= 0 || !ts?.stop_loss || ts.stop_loss <= 0) return null;

  const entry = ts.entry;
  const stopLoss = ts.stop_loss;
  const target = ts.target || entry * 1.1;
  const rr = ts.risk_reward || 0;
  const signal = an?.signal || "HOLD";
  const score = an?.score || 50;
  const trend = an?.trend || "UNKNOWN";
  const rsi = ind?.rsi || 50;
  const price = analysis?.price || entry;
  const marketCap = si?.market_cap || 0;

  // --- 1. Base risk % based on signal strength ---
  let riskPct = signal === "STRONG_BUY" ? 0.025 : signal === "BUY" ? 0.02 : 0.01;

  // --- 2. Penny stock / market cap adjustment ---
  let capCategory, capMultiplier;
  if (price < 1) {
    capCategory = "PENNY"; capMultiplier = 0.3;
  } else if (price < 5 || (marketCap > 0 && marketCap < 300e6)) {
    capCategory = "MICRO"; capMultiplier = 0.5;
  } else if (price < 20 || (marketCap > 0 && marketCap < 2e9)) {
    capCategory = "SMALL"; capMultiplier = 0.7;
  } else if (marketCap > 0 && marketCap < 10e9) {
    capCategory = "MID"; capMultiplier = 0.85;
  } else {
    capCategory = "LARGE"; capMultiplier = 1.0;
  }
  riskPct *= capMultiplier;

  // --- 3. R/R quality boost ---
  if (rr >= 4) riskPct *= 1.3;
  else if (rr >= 3) riskPct *= 1.2;
  else if (rr >= 2) riskPct *= 1.1;
  else if (rr < 1 && rr > 0) riskPct *= 0.6;

  // --- 4. Trend & score adjustment ---
  if (trend === "UP" && score >= 70) riskPct *= 1.15;
  else if (trend === "DOWN" || score < 40) riskPct *= 0.7;

  // --- 5. RSI extremes ---
  if (rsi > 80) riskPct *= 0.5; // overbought = very risky
  else if (rsi > 70) riskPct *= 0.7;
  else if (rsi < 25) riskPct *= 1.2; // oversold bounce opportunity

  // --- 6. Volatility (stop distance as % of entry) ---
  const stopPct = Math.abs(entry - stopLoss) / entry;
  if (stopPct > 0.15) riskPct *= 0.5; // very wide stop = risky
  else if (stopPct > 0.10) riskPct *= 0.7;

  // Cap risk at absolute bounds
  riskPct = Math.max(0.003, Math.min(riskPct, 0.035));

  // --- Position calculation ---
  const riskPerShare = Math.abs(entry - stopLoss);
  if (riskPerShare <= 0) return null;
  const riskAmount = portfolioValue * riskPct;
  const shares = Math.floor(riskAmount / riskPerShare);
  let positionValue = shares * entry;

  // Max allocation cap by category
  const maxPctMap = { PENNY: 0.02, MICRO: 0.04, SMALL: 0.06, MID: 0.08, LARGE: 0.10 };
  const maxPct = signal === "STRONG_BUY" ? maxPctMap[capCategory] * 1.3 : maxPctMap[capCategory];
  if (positionValue > portfolioValue * maxPct) positionValue = Math.floor(portfolioValue * maxPct);

  // Min threshold
  if (positionValue < 50) return null;

  const potentialGain = target > entry ? ((target - entry) / entry * positionValue) : 0;
  const potentialLoss = riskPerShare / entry * positionValue;

  // Build reasoning tags
  const tags = [];
  if (capCategory === "PENNY") tags.push("Penny stock — position mini");
  else if (capCategory === "MICRO") tags.push("Micro-cap — position réduite");
  else if (capCategory === "SMALL") tags.push("Small-cap — position prudente");
  else if (capCategory === "LARGE") tags.push("Blue chip — position standard");
  if (rr >= 3) tags.push(`R/R ${rr}:1 excellent`);
  else if (rr < 1.5 && rr > 0) tags.push("R/R faible — prudence");
  if (rsi > 70) tags.push("RSI suracheté — réduit");
  else if (rsi < 30) tags.push("RSI survendu — opportunité");
  if (trend === "UP" && score >= 70) tags.push("Tendance haussière forte");
  if (stopPct > 0.10) tags.push("Stop large — volatilité élevée");

  return {
    amount: Math.round(positionValue / 100) * 100 || 100,
    pctPortfolio: ((positionValue / portfolioValue) * 100).toFixed(1),
    potentialGain: Math.round(potentialGain),
    potentialLoss: Math.round(potentialLoss),
    riskPctUsed: (riskPct * 100).toFixed(1),
    capCategory,
    tags,
  };
}

// Revenue & Margin History Chart
function RevenueMarginChart({ ticker }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    finnhubAPI.getFinancialsHistory(ticker).then(res => {
      setData(res?.history || []);
    }).catch(() => setData([])).finally(() => setLoading(false));
  }, [ticker]);

  if (loading) return <div className="glass-card rounded-lg p-4 mb-4"><div className="text-xs text-slate-500 animate-pulse">Chargement historique financier...</div></div>;
  if (!data || data.length === 0) return null;

  const fmtB = (v) => { if (!v) return "—"; if (v >= 1e9) return `${(v/1e9).toFixed(1)}B`; if (v >= 1e6) return `${(v/1e6).toFixed(0)}M`; return v.toLocaleString(); };

  return (
    <div className="glass-card rounded-lg p-4 mb-4" data-testid="revenue-margin-chart">
      <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-cyan-400" /> Evolution CA & Marge Nette (5 ans)
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <XAxis dataKey="year" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="rev" orientation="left" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtB(v)} />
            <YAxis yAxisId="margin" orientation="right" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <RechartsTooltip
              contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
              formatter={(value, name) => {
                if (name === "revenue") return [`$${fmtB(value)}`, "Chiffre d'affaires"];
                if (name === "net_margin") return [`${value}%`, "Marge Nette"];
                if (name === "gross_margin") return [`${value}%`, "Marge Brute"];
                return [value, name];
              }}
            />
            <Bar yAxisId="rev" dataKey="revenue" fill="#06b6d4" radius={[4, 4, 0, 0]} opacity={0.7} />
            <Line yAxisId="margin" type="monotone" dataKey="net_margin" stroke="#22c55e" strokeWidth={2.5} dot={{ fill: '#22c55e', r: 4 }} />
            <Line yAxisId="margin" type="monotone" dataKey="gross_margin" stroke="#a78bfa" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-2 justify-center">
        <span className="text-[10px] text-cyan-400 flex items-center gap-1"><span className="w-3 h-2 bg-cyan-500 rounded-sm inline-block" /> CA</span>
        <span className="text-[10px] text-emerald-400 flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block" /> Marge Nette</span>
        <span className="text-[10px] text-purple-400 flex items-center gap-1"><span className="w-3 h-0.5 bg-purple-400 inline-block border-dashed" /> Marge Brute</span>
      </div>
    </div>
  );
}

// Insider Activity Panel
function InsiderActivity({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    finnhubAPI.getInsiderActivity(ticker).then(res => {
      setData(res);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [ticker]);

  if (loading) return <div className="glass-card rounded-lg p-4 mb-4"><div className="text-xs text-slate-500 animate-pulse">Chargement insiders...</div></div>;
  if (!data || !data.transactions || data.transactions.length === 0) return null;

  const s = data.summary || {};
  const signalColor = s.signal === "BULLISH" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" : s.signal === "BEARISH" ? "text-red-400 bg-red-500/10 border-red-500/30" : "text-slate-400 bg-slate-700/50 border-slate-600/50";
  const fmtVal = (v) => { if (!v) return "$0"; if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${v}`; };

  return (
    <div className="glass-card rounded-lg p-4 mb-4" data-testid="insider-activity">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-yellow-400" /> Insider Activity (6 mois)
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${signalColor}`}>{s.signal}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center p-2 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500">Achats</div>
          <div className="text-lg font-bold text-emerald-400">{s.total_buys || 0}</div>
          <div className="text-[9px] text-slate-500">{fmtVal(s.buy_value)}</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500">Ventes</div>
          <div className="text-lg font-bold text-red-400">{s.total_sells || 0}</div>
          <div className="text-[9px] text-slate-500">{fmtVal(s.sell_value)}</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500">Net</div>
          <div className={`text-lg font-bold ${(s.net || 0) > 0 ? "text-emerald-400" : (s.net || 0) < 0 ? "text-red-400" : "text-slate-400"}`}>
            {(s.net || 0) > 0 ? "+" : ""}{s.net || 0}
          </div>
        </div>
      </div>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {data.transactions.slice(0, 8).map((t, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-800/30 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.type === "BUY" ? "bg-emerald-400" : "bg-red-400"}`} />
              <span className="text-slate-300 truncate max-w-[140px]">{t.name}</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`font-mono ${t.type === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
                {t.type === "BUY" ? "+" : "-"}{t.shares?.toLocaleString()}
              </span>
              <span className="text-slate-500 w-16 text-right">{t.date?.slice(5)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalystSnapshot({ ticker, currency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    finnhubAPI.getAnalystSnapshot(ticker).then(res => {
      setData(res && !res.error ? res : null);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [ticker]);

  if (loading) return <div className="glass-card rounded-lg p-4 mb-4"><div className="text-xs text-slate-500 animate-pulse">Chargement analystes...</div></div>;
  if (!data) return null;

  const a = data.analyst || {};
  const e = data.earnings || {};
  const s = data.short_interest || {};
  const curr = currency || "$";

  const recoColor = a.verdict === "STRONG_BUY" ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/40"
    : a.verdict === "BUY" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
    : a.verdict === "HOLD" ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
    : a.verdict === "SELL" ? "text-red-400 bg-red-500/10 border-red-500/30"
    : a.verdict === "STRONG_SELL" ? "text-red-300 bg-red-500/15 border-red-500/40"
    : "text-slate-400 bg-slate-700/50 border-slate-600/50";

  const upsideColor = a.upside_pct == null ? "text-slate-400"
    : a.upside_pct > 15 ? "text-emerald-400"
    : a.upside_pct > 0 ? "text-emerald-300"
    : a.upside_pct > -10 ? "text-amber-400"
    : "text-red-400";

  const earningsImminent = e.days_until != null && e.days_until >= 0 && e.days_until <= 7;
  const earningsColor = earningsImminent ? "text-amber-300 bg-amber-500/15 border-amber-500/40" : "text-slate-300 bg-slate-700/40 border-slate-600/40";

  const shortColor = s.verdict === "HIGH_SHORT_RISK" ? "text-red-300 bg-red-500/15 border-red-500/40"
    : s.verdict === "ELEVATED_SHORT" ? "text-amber-300 bg-amber-500/10 border-amber-500/30"
    : "text-slate-300 bg-slate-700/40 border-slate-600/40";

  const fmtPct = (v) => v == null ? "—" : `${(v * 100).toFixed(1)}%`;
  const lastSurp = e.last_surprise || {};
  const surprisePct = lastSurp.surprise_pct;
  const beat = surprisePct != null && surprisePct > 0;

  return (
    <div className="glass-card rounded-lg p-4 mb-4" data-testid="analyst-snapshot-panel">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-cyan-400" />
        <span className="text-xs text-cyan-400 font-bold uppercase tracking-wider">Snapshot Pro</span>
        <span className="text-[9px] text-slate-600 ml-1">Analystes &amp; Résultats &amp; Short</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* ======== ANALYSTES ======== */}
        <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40" data-testid="analyst-targets-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Cible Analystes</span>
            {a.verdict !== "N/A" && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${recoColor}`}>{a.verdict?.replace("_", " ")}</span>}
          </div>
          {a.target_mean != null ? (
            <>
              <div className="font-mono text-xl font-black text-white">{curr}{a.target_mean.toFixed(2)}</div>
              <div className={`text-xs font-bold ${upsideColor}`}>
                {a.upside_pct != null && (a.upside_pct >= 0 ? "+" : "")}{a.upside_pct != null ? `${a.upside_pct.toFixed(1)}%` : "—"} potentiel
              </div>
              <div className="text-[10px] text-slate-500 mt-2 space-y-0.5">
                <div className="flex justify-between"><span>Range</span><span className="font-mono">{curr}{a.target_low?.toFixed(0)}–{curr}{a.target_high?.toFixed(0)}</span></div>
                <div className="flex justify-between"><span>Analystes</span><span className="font-mono">{a.num_analysts || "—"}</span></div>
                <div className="flex justify-between"><span>Reco moy.</span><span className="font-mono">{a.recommendation_mean?.toFixed(2) || "—"}/5</span></div>
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 mt-2">Pas de couverture analyste</div>
          )}
        </div>

        {/* ======== EARNINGS ======== */}
        <div className={`rounded-lg p-3 border ${earningsImminent ? "bg-amber-500/10 border-amber-500/30" : "bg-slate-800/40 border-slate-700/40"}`} data-testid="earnings-calendar-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Prochains Résultats</span>
            {earningsImminent && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40">IMMINENT</span>}
          </div>
          {e.next_date ? (
            <>
              <div className="font-mono text-base font-bold text-white">{e.next_date.slice(0, 10)}</div>
              <div className={`text-xs font-bold ${earningsColor.split(" ")[0]}`}>
                {e.days_until != null && e.days_until >= 0 ? `dans ${e.days_until}j` : (e.days_until != null && e.days_until < 0 ? `il y a ${-e.days_until}j` : "—")}
              </div>
              <div className="text-[10px] text-slate-500 mt-2 space-y-0.5">
                {lastSurp.eps_actual != null && (
                  <>
                    <div className="flex justify-between"><span>Dernier BPA</span><span className={`font-mono ${beat ? "text-emerald-400" : "text-red-400"}`}>{lastSurp.eps_actual.toFixed(2)}{lastSurp.eps_estimate != null && ` (est ${lastSurp.eps_estimate.toFixed(2)})`}</span></div>
                    {surprisePct != null && (
                      <div className="flex justify-between"><span>Surprise</span><span className={`font-mono font-bold ${beat ? "text-emerald-400" : "text-red-400"}`}>{beat ? "+" : ""}{(surprisePct * 100).toFixed(1)}%</span></div>
                    )}
                  </>
                )}
                {e.trailing_eps != null && <div className="flex justify-between"><span>BPA TTM</span><span className="font-mono">{e.trailing_eps.toFixed(2)}</span></div>}
                {e.forward_eps != null && <div className="flex justify-between"><span>BPA forward</span><span className="font-mono">{e.forward_eps.toFixed(2)}</span></div>}
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 mt-2">Date non disponible</div>
          )}
        </div>

        {/* ======== SHORT INTEREST ======== */}
        <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40" data-testid="short-interest-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Short Interest</span>
            {s.verdict !== "N/A" && s.verdict !== "NORMAL" && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${shortColor}`}>{s.verdict?.replace("_", " ")}</span>}
          </div>
          {s.short_percent_of_float != null ? (
            <>
              <div className={`font-mono text-xl font-black ${s.verdict === "HIGH_SHORT_RISK" ? "text-red-400" : s.verdict === "ELEVATED_SHORT" ? "text-amber-400" : "text-white"}`}>
                {fmtPct(s.short_percent_of_float)}
              </div>
              <div className="text-xs text-slate-400">du flottant shorté</div>
              <div className="text-[10px] text-slate-500 mt-2 space-y-0.5">
                <div className="flex justify-between"><span>Days to cover</span><span className="font-mono">{s.short_ratio_days_to_cover?.toFixed(1) || "—"}j</span></div>
                <div className="flex justify-between"><span>Évolution 1m</span>
                  <span className={`font-mono ${s.month_change_pct != null && s.month_change_pct > 10 ? "text-red-400" : s.month_change_pct != null && s.month_change_pct < -10 ? "text-emerald-400" : "text-slate-400"}`}>
                    {s.month_change_pct != null ? `${s.month_change_pct > 0 ? "+" : ""}${s.month_change_pct.toFixed(0)}%` : "—"}
                  </span>
                </div>
                {s.shares_short != null && <div className="flex justify-between"><span>Actions short</span><span className="font-mono">{(s.shares_short / 1e6).toFixed(1)}M</span></div>}
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 mt-2">Données indisponibles</div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function AlgoTab({
  algoTicker, setAlgoTicker,
  algoAnalysis, setAlgoAnalysis,
  algoLoading, algoHistory,
  chartData, chartTimeframe, setChartTimeframe, chartLoading,
  stockPerf, stockFundamentals, fundamentalsPro,
  backtestResult, backtestStrategy, setBacktestStrategy,
  backtestConclusion, setBacktestConclusion,
  portfolioOptimization,
  runAlgoAnalysis, runBacktest, runOptimization,
  onGetBacktestConclusion,
  ownerPositions, prices, valuationData,
  onSendToCommittee,
  onSetPriceAlert,
  topRated,
  portfolioValue,
}) {
  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-4 sm:mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 sm:gap-3"><Cpu className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />Algo Trading</h1>
          <p className="text-slate-400 mt-1 text-xs sm:text-sm">Technical analysis, backtesting & portfolio optimization</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
        {/* Left: Analysis */}
        <div className="lg:col-span-8 space-y-4 sm:space-y-6">
          {/* Smart Stock Search */}
          <div className="glass-card rounded-xl p-3 sm:p-5 overflow-visible relative z-30">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-end">
              <StockSearch value={algoTicker} onSelect={(ticker) => { setAlgoTicker(ticker); runAlgoAnalysis(ticker); }} placeholder="NVDA, Apple, Thales..." />
              <div className="flex gap-2">
                <button onClick={() => runAlgoAnalysis(algoTicker)} disabled={algoLoading}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-bold rounded-lg flex items-center justify-center gap-2 shadow-glow hover:shadow-glow-lg text-sm">
                  {algoLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}Analyser
                </button>
                <button onClick={() => runAlgoAnalysis(algoTicker, true)} disabled={algoLoading}
                  className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700 text-slate-300 font-semibold rounded-lg flex items-center gap-2 hover:bg-slate-600 transition-colors"
                  title="Forcer une nouvelle analyse (ignorer le cache)" data-testid="algo-refresh-btn">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Loading Skeleton */}
          {!algoAnalysis && algoLoading && (
            <div className="glass-card rounded-xl p-5 animate-pulse" data-testid="algo-loading-skeleton">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="h-6 w-24 bg-slate-700 rounded mb-2" />
                  <div className="h-4 w-40 bg-slate-800 rounded mb-3" />
                  <div className="h-10 w-36 bg-slate-700 rounded" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-12 bg-slate-700 rounded" />
                  <div className="h-6 w-20 bg-slate-800 rounded" />
                </div>
              </div>
              <div className="h-12 w-full bg-slate-800/50 rounded-lg mb-4" />
              <div className="h-[320px] bg-slate-800/30 rounded-lg mb-4 flex items-center justify-center">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
                  <span className="text-sm text-slate-400">Analyse en cours...</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mb-4">
                {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-800/50 rounded-lg" />)}
              </div>
              <div className="h-24 bg-slate-800/30 rounded-lg" />
            </div>
          )}

          {/* Analysis Results */}
          {algoAnalysis && algoAnalysis.error && (
            <div className="glass-card rounded-xl p-5" data-testid="algo-error-banner">
              <div className="flex items-center gap-3 text-amber-400 mb-3">
                <ShieldAlert className="w-5 h-5" />
                <h3 className="font-bold text-base">{algoAnalysis.ticker}</h3>
              </div>
              <p className="text-sm text-slate-300 mb-2">{algoAnalysis.error}</p>
              <p className="text-xs text-slate-500">Ce ticker n'a pas assez de donnees historiques ou n'est pas disponible sur Yahoo Finance / Finnhub. Essayez un autre ticker ou reessayez plus tard.</p>
              <button onClick={() => runAlgoAnalysis(algoTicker, true)} className="mt-3 px-4 py-1.5 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded hover:bg-cyan-500/30 transition-colors">
                <RefreshCw className="w-3 h-3 inline mr-1" />Reessayer
              </button>
            </div>
          )}
          {algoAnalysis && !algoAnalysis.error && (() => {
            const curr = algoAnalysis.currency ? ({"EUR":"€","GBP":"£","CHF":"CHF ","SEK":"kr ","NOK":"kr ","DKK":"kr ","HKD":"HK$","JPY":"¥","KRW":"₩","AUD":"A$","INR":"₹"}[algoAnalysis.currency] || "$") : getTickerCurrency(algoAnalysis.ticker);
            return (
            <div className="glass-card rounded-xl p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4 sm:mb-6">
                <div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <h3 className="font-display text-lg sm:text-xl font-bold">{algoAnalysis.ticker}</h3>
                    {algoAnalysis.sector_info?.sector && <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{algoAnalysis.sector_info.sector}</span>}
                    {algoAnalysis.sector_info?.category && !algoAnalysis.sector_info?.sector && <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-slate-700 text-slate-300 border border-slate-600">{algoAnalysis.sector_info.category}</span>}
                  </div>
                  {algoAnalysis.sector_info?.long_name && <div className="text-xs text-slate-400 mt-0.5">{algoAnalysis.sector_info.long_name}</div>}
                  {algoAnalysis.sector_info?.industry && (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] text-slate-500">{algoAnalysis.sector_info.industry}</span>
                      {algoAnalysis.sector_info?.country && <><span className="text-[10px] text-slate-600">|</span><span className="text-[10px] text-slate-500">{algoAnalysis.sector_info.country}</span></>}
                      {algoAnalysis.sector_info?.market_cap && (
                        <><span className="text-[10px] text-slate-600">|</span><span className="text-[10px] text-slate-500">Cap: {curr}{algoAnalysis.sector_info.market_cap >= 1e12 ? (algoAnalysis.sector_info.market_cap / 1e12).toFixed(1) + "T" : algoAnalysis.sector_info.market_cap >= 1e9 ? (algoAnalysis.sector_info.market_cap / 1e9).toFixed(0) + "B" : (algoAnalysis.sector_info.market_cap / 1e6).toFixed(0) + "M"}</span></>
                      )}
                    </div>
                  )}
                  <div className="flex items-baseline gap-3 mt-1">
                    <div className="text-2xl sm:text-3xl font-mono font-bold text-white">{curr}{formatNumber(algoAnalysis.price)}</div>
                    {stockPerf._ticker === algoAnalysis.ticker && stockPerf["1J"] != null && (
                      <div className={`text-base sm:text-lg font-mono font-bold ${stockPerf["1J"] >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="algo-daily-change">
                        {stockPerf["1J"] >= 0 ? "+" : ""}{stockPerf["1J"].toFixed(2)}%
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex sm:flex-col items-center gap-2 sm:text-right">
                  <ScoreGauge score={algoAnalysis.analysis?.score || 0} size={80} />
                  <SignalBadge signal={algoAnalysis.analysis?.signal || "HOLD"} />
                </div>
              </div>

              {/* Portfolio Position Banner */}
              {(() => {
                const myPos = ownerPositions.find(p => p.ticker === algoAnalysis.ticker);
                if (!myPos) return (
                  <div className="mb-4 p-3 rounded-lg bg-slate-700/30 border border-slate-600/50 flex items-center gap-3">
                    <Wallet className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-400">Pas de position — Opportunité d'entrée ?</span>
                  </div>
                );
                const pr = algoAnalysis.price || prices[myPos.ticker] || myPos.avg;
                const pnl = (pr - myPos.avg) * myPos.qty;
                const pnlPct = ((pr - myPos.avg) / myPos.avg * 100);
                const value = pr * myPos.qty;
                return (
                  <div className={`mb-4 p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${pnl >= 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                      <Wallet className={`w-4 h-4 ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`} />
                      <span className="text-sm font-bold text-white">Ma Position</span>
                      <span className="text-xs text-slate-300">{myPos.qty} actions @ {curr}{formatNumber(myPos.avg)}</span>
                      <span className="text-xs text-slate-400">({(myPos.strategy || "LT") === "LT" ? "Long Term" : "Swing"})</span>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-5">
                      <div className="text-right"><div className="text-[9px] text-slate-500">Valeur</div><div className="font-mono text-sm text-white">{curr}{Math.round(value).toLocaleString()}</div></div>
                      <div className="text-right"><div className="text-[9px] text-slate-500">P&L</div><div className={`font-mono text-sm font-bold ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pnl >= 0 ? "+" : ""}{Math.round(pnl).toLocaleString()}{curr.trim()} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)</div></div>
                      {myPos.stop_loss && <div className="text-right"><div className="text-[9px] text-slate-500">Stop Loss</div><div className="font-mono text-sm text-red-400">{curr}{formatNumber(myPos.stop_loss)}</div></div>}
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 sm:mb-6">
                {/* Price Chart */}
                <div className="col-span-2 sm:col-span-4 bg-slate-800/50 rounded-lg p-3 sm:p-4 mb-2">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                    <div className="text-xs text-cyan-400 font-bold uppercase tracking-wider flex items-center gap-2"><Crosshair className="w-4 h-4" />Graphique — Niveaux Clés</div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                      {["1D", "1W", "1M", "3M", "6M", "1Y"].map(tf => (
                        <button key={tf} data-testid={`chart-tf-${tf}`} onClick={() => setChartTimeframe(tf)}
                          className={`px-2 sm:px-2.5 py-1 rounded text-[10px] font-bold transition-all ${chartTimeframe === tf ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "text-slate-500 hover:text-slate-300"}`}>
                          {tf === "1D" ? "1J" : tf === "1W" ? "1S" : tf}
                        </button>
                      ))}
                      <div className="hidden sm:block w-px h-4 bg-slate-700 mx-1" />
                      <div className="hidden sm:flex items-center gap-3 text-[9px]">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />Support</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />Resistance</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" />Entrée</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Target</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400 inline-block" />Mon Entry</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-1 bg-amber-500 inline-block rounded-sm" />Vol+</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-1 bg-red-500 inline-block rounded-sm" />Vol-</span>
                      </div>
                    </div>
                  </div>
                  {chartLoading ? (
                    <div className="h-[320px] flex items-center justify-center text-cyan-400 text-sm">Chargement...</div>
                  ) : (chartData || algoAnalysis.chart_data)?.closes?.length > 0 ? (() => {
                    const cd = chartData || algoAnalysis.chart_data;
                    const myPos = ownerPositions.find(p => p.ticker === algoAnalysis.ticker);
                    const maxVol = Math.max(...(cd.volumes || []).map(v => v || 0), 1);
                    const avgVol = (cd.volumes || []).reduce((a, b) => a + (b || 0), 0) / Math.max((cd.volumes || []).length, 1);
                    const chartItems = cd.closes.map((c, i) => {
                      const ts = cd.timestamps?.[i];
                      const isIntraday = ["1D", "1W", "1M"].includes(chartTimeframe);
                      const vol = cd.volumes?.[i] || 0;
                      return {
                        date: ts ? new Date(ts * 1000).toLocaleDateString("fr-FR", isIntraday ? { hour: "2-digit", minute: "2-digit" } : { day: "2-digit", month: "short" }) : i,
                        price: c, high: cd.highs?.[i], low: cd.lows?.[i], volume: vol,
                        volColor: vol >= avgVol * 1.3 ? "#f59e0b" : vol >= avgVol * 0.7 ? "#d97706" : "#ef4444",
                      };
                    });
                    return (
                      <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart data={chartItems} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                          <defs><linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} /><stop offset="95%" stopColor="#22d3ee" stopOpacity={0} /></linearGradient></defs>
                          <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                          <YAxis yAxisId="price" domain={['auto', 'auto']} tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} width={50} tickFormatter={v => `${curr}${v}`} />
                          <YAxis yAxisId="volume" orientation="right" hide domain={[0, maxVol * 5]} />
                          <RechartsTooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
                            formatter={(v, name) => { if (name === 'volume') return [Number(v).toLocaleString(), 'Vol']; return [`${curr}${Number(v).toFixed(2)}`, name === 'price' ? 'Prix' : name === 'high' ? 'Haut' : 'Bas']; }}
                            labelStyle={{ color: '#94a3b8' }} />
                          <Bar yAxisId="volume" dataKey="volume" opacity={0.7} radius={[1, 1, 0, 0]}>
                            {chartItems.map((entry, idx) => (<Cell key={idx} fill={entry.volColor} />))}
                          </Bar>
                          <Area yAxisId="price" type="monotone" dataKey="price" stroke="#22d3ee" strokeWidth={2} fill="url(#priceGrad)" dot={false} />
                          {algoAnalysis.levels?.support && <ReferenceLine yAxisId="price" y={algoAnalysis.levels.support} stroke="#34d399" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `S ${curr}${algoAnalysis.levels.support}`, position: 'right', fontSize: 9, fill: '#34d399' }} />}
                          {algoAnalysis.levels?.resistance && <ReferenceLine yAxisId="price" y={algoAnalysis.levels.resistance} stroke="#f87171" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `R ${curr}${algoAnalysis.levels.resistance}`, position: 'right', fontSize: 9, fill: '#f87171' }} />}
                          {algoAnalysis.trade_setup?.entry && <ReferenceLine yAxisId="price" y={algoAnalysis.trade_setup.entry} stroke="#22d3ee" strokeDasharray="3 3" strokeWidth={1} label={{ value: `Entrée ${curr}${algoAnalysis.trade_setup.entry}`, position: 'left', fontSize: 9, fill: '#22d3ee' }} />}
                          {algoAnalysis.trade_setup?.target && <ReferenceLine yAxisId="price" y={algoAnalysis.trade_setup.target} stroke="#fbbf24" strokeDasharray="3 3" strokeWidth={1} label={{ value: `Target ${curr}${algoAnalysis.trade_setup.target}`, position: 'left', fontSize: 9, fill: '#fbbf24' }} />}
                          {algoAnalysis.trade_setup?.stop_loss && <ReferenceLine yAxisId="price" y={algoAnalysis.trade_setup.stop_loss} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1} label={{ value: `SL ${curr}${algoAnalysis.trade_setup.stop_loss}`, position: 'left', fontSize: 9, fill: '#ef4444' }} />}
                          {myPos?.avg && <ReferenceLine yAxisId="price" y={myPos.avg} stroke="#a78bfa" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: `Mon Entry ${curr}${formatNumber(myPos.avg)}`, position: 'insideTopRight', fontSize: 9, fill: '#a78bfa' }} />}
                        </ComposedChart>
                      </ResponsiveContainer>
                    );
                  })() : (
                    <div className="h-[280px] flex items-center justify-center text-slate-600 text-sm">Chargement du graphique...</div>
                  )}
                </div>
              </div>

              {/* Performance by Period */}
              {stockPerf._ticker === algoAnalysis.ticker && (
                <div className="glass-card rounded-lg p-3 mb-4" data-testid="stock-performance-bar">
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-0 divide-x divide-slate-700">
                    {[["1J","1 jour"],["1S","1 sem"],["1M","1 mois"],["3M","3 mois"],["6M","6 mois"],["1A","1 an"],["5A","5 ans"],["Max","Max"]].map(([k, label]) => {
                      const val = stockPerf[k];
                      return (
                        <div key={k} className="text-center px-1 py-1">
                          <div className="text-[9px] sm:text-[10px] text-slate-400 font-medium">{label}</div>
                          <div className={`font-mono text-xs sm:text-sm font-bold ${val == null ? "text-slate-600" : val >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {val == null ? "—" : `${val >= 0 ? "+" : ""}${val.toFixed(1)}%`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KPIs Fondamentaux */}
              {stockFundamentals?._ticker === algoAnalysis.ticker && stockFundamentals?.kpis && (
                <div className="glass-card rounded-lg p-4 mb-4" data-testid="stock-kpis">
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">KPIs Fondamentaux</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2">
                    {(() => {
                      const k = stockFundamentals.kpis;
                      const fmtNum = (v) => { if (v == null) return "—"; if (Math.abs(v) >= 1e12) return `${(v/1e12).toFixed(1)}T`; if (Math.abs(v) >= 1e9) return `${(v/1e9).toFixed(1)}B`; if (Math.abs(v) >= 1e6) return `${(v/1e6).toFixed(0)}M`; return v.toLocaleString("fr-FR"); };
                      const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : "—";
                      const fmtX = (v) => v != null ? `${Number(v).toFixed(1)}x` : "—";
                      const kpiRows = [
                        ["Capitalisation", fmtNum(k.market_cap)], ["PER", fmtX(k.pe_ratio)], ["Forward PER", fmtX(k.forward_pe)],
                        ["BPA (EPS)", k.eps != null ? `${curr}${k.eps.toFixed(2)}` : "—"], ["Chiffre d'affaires", fmtNum(k.revenue)],
                        ["Croissance CA", fmtPct(k.revenue_growth), k.revenue_growth], ["Croissance BPA", fmtPct(k.earnings_growth), k.earnings_growth],
                        ["Marge nette", fmtPct(k.profit_margin)], ["EBITDA", fmtNum(k.ebitda)], ["EV/EBITDA", fmtX(k.ev_ebitda)],
                        ["Dette totale", fmtNum(k.total_debt)], ["D/E Ratio", k.debt_to_equity != null ? `${k.debt_to_equity.toFixed(0)}%` : "—", k.debt_to_equity != null ? -k.debt_to_equity : null],
                        ["ROE", fmtPct(k.roe), k.roe], ["ROA", fmtPct(k.roa), k.roa], ["FCF", fmtNum(k.free_cash_flow), k.free_cash_flow],
                        ["Dividende", k.dividend_yield != null ? `${(k.dividend_yield * 100).toFixed(2)}%` : "—"],
                        ["Beta", k.beta != null ? k.beta.toFixed(2) : "—"], ["P/B", fmtX(k.pb_ratio)],
                        ["Ecart 52 sem.", k.week_52_low != null && k.week_52_high != null ? `${curr}${k.week_52_low.toFixed(0)} - ${curr}${k.week_52_high.toFixed(0)}` : "—"],
                        ["Trésorerie", fmtNum(k.total_cash)],
                      ];
                      return kpiRows.map(([label, val, colorVal], i) => (
                        <div key={i} className="flex justify-between items-center py-1 border-b border-slate-800/50">
                          <span className="text-xs text-slate-400">{label}</span>
                          <span className={`font-mono text-xs font-semibold ${colorVal != null ? (colorVal > 0 ? "text-emerald-400" : "text-red-400") : "text-white"}`}>{val}</span>
                        </div>
                      ));
                    })()}
                  </div>
                  {stockFundamentals.sector && (
                    <div className="mt-3 flex gap-2">
                      <span className="text-[10px] px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-full">{stockFundamentals.sector}</span>
                      {stockFundamentals.industry && <span className="text-[10px] px-2 py-1 bg-slate-700 text-slate-400 rounded-full">{stockFundamentals.industry}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* Fundamentals Pro Panel */}
              {fundamentalsPro?._ticker === algoAnalysis.ticker && (
                <FundamentalsProPanel data={fundamentalsPro} />
              )}

              {/* Revenue & Margin History Chart */}
              <RevenueMarginChart ticker={algoAnalysis.ticker} />

              {/* Insider Activity */}
              <InsiderActivity ticker={algoAnalysis.ticker} />

              {/* Analyst Targets + Earnings + Short Interest */}
              <AnalystSnapshot ticker={algoAnalysis.ticker} currency={curr} />

              {/* Quick Decision Panel */}
              <div className={`rounded-lg p-3 sm:p-4 mb-4 border ${algoAnalysis.analysis?.signal === "BUY" ? "bg-emerald-500/10 border-emerald-500/30" : algoAnalysis.analysis?.signal === "SELL" ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className={`text-2xl font-black ${algoAnalysis.analysis?.signal === "BUY" ? "text-emerald-400" : algoAnalysis.analysis?.signal === "SELL" ? "text-red-400" : "text-amber-400"}`}>
                      {algoAnalysis.analysis?.signal === "BUY" ? "ENTRER" : algoAnalysis.analysis?.signal === "SELL" ? "NE PAS ENTRER" : "ATTENDRE"}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300">
                      {algoAnalysis.analysis?.signal === "BUY" 
                        ? `Zone d'entrée: ${curr}${algoAnalysis.trade_setup?.entry} — Target: ${curr}${algoAnalysis.trade_setup?.target} (+${algoAnalysis.trade_setup?.target && algoAnalysis.trade_setup?.entry ? ((algoAnalysis.trade_setup.target - algoAnalysis.trade_setup.entry) / algoAnalysis.trade_setup.entry * 100).toFixed(1) : 0}%)`
                        : algoAnalysis.analysis?.signal === "SELL"
                        ? "Signal technique négatif — attendre un meilleur point d'entrée"
                        : algoAnalysis.trade_setup?.entry && algoAnalysis.trade_setup?.entry < algoAnalysis.trade_setup?.current_price
                          ? `Zone d'achat: ${curr}${algoAnalysis.trade_setup.entry} (${((1 - algoAnalysis.trade_setup.entry / algoAnalysis.trade_setup.current_price) * 100).toFixed(1)}% pullback) — ${algoAnalysis.trade_setup.entry_reason || `Target: ${curr}${algoAnalysis.trade_setup?.target}`}`
                          : `Entrée possible au prix actuel — Target: ${curr}${algoAnalysis.trade_setup?.target || algoAnalysis.levels?.resistance || "—"}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Price Alert Button */}
                    {onSetPriceAlert && (
                      <button
                        onClick={() => {
                          const sig = algoAnalysis.analysis?.signal;
                          const targetPrice = sig === "BUY" 
                            ? algoAnalysis.trade_setup?.entry
                            : sig === "SELL"
                            ? algoAnalysis.trade_setup?.stop_loss || algoAnalysis.levels?.support
                            : algoAnalysis.trade_setup?.entry || algoAnalysis.levels?.support;
                          const direction = sig === "BUY" ? "below" : sig === "SELL" ? "below" : "below";
                          const label = sig === "BUY" ? "ENTRER" : sig === "SELL" ? "NE PAS ENTRER" : "ATTENDRE";
                          const note = sig === "BUY"
                            ? `Entrée à ${curr}${algoAnalysis.trade_setup?.entry} — Target ${curr}${algoAnalysis.trade_setup?.target}`
                            : sig === "SELL"
                            ? "Signal négatif"
                            : `Zone d'achat: ${curr}${algoAnalysis.trade_setup?.entry || algoAnalysis.levels?.support}`;
                          if (targetPrice) {
                            onSetPriceAlert({
                              ticker: algoAnalysis.ticker,
                              target_price: parseFloat(targetPrice),
                              direction,
                              signal: label,
                              note,
                            });
                          }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-all text-xs font-bold whitespace-nowrap"
                        data-testid="set-price-alert-btn"
                      >
                        <Bell className="w-3.5 h-3.5" />
                        Alerter
                      </button>
                    )}
                    <div className="text-right">
                      <div className="text-xs text-slate-400">Risk/Reward</div>
                      <div className="font-mono text-lg sm:text-xl font-bold text-white">{algoAnalysis.trade_setup?.risk_reward || "—"}:1</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4"><div className="text-xs text-slate-400 mb-1">RSI (14)</div><div className={`font-mono text-lg sm:text-xl font-bold ${algoAnalysis.indicators?.rsi < 30 ? "text-emerald-400" : algoAnalysis.indicators?.rsi > 70 ? "text-red-400" : "text-slate-100"}`}>{algoAnalysis.indicators?.rsi || "—"}</div></div>
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4"><div className="text-xs text-slate-400 mb-1">MACD</div><div className={`font-mono text-lg sm:text-xl font-bold ${algoAnalysis.indicators?.macd > 0 ? "text-emerald-400" : "text-red-400"}`}>{algoAnalysis.indicators?.macd?.toFixed(2) || "—"}</div></div>
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4"><div className="text-xs text-slate-400 mb-1">Support</div><div className="font-mono text-lg sm:text-xl font-bold text-emerald-400">${algoAnalysis.levels?.support || "—"}</div></div>
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4"><div className="text-xs text-slate-400 mb-1">Résistance</div><div className="font-mono text-lg sm:text-xl font-bold text-red-400">${algoAnalysis.levels?.resistance || "—"}</div></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4">
                  <div className="text-xs text-slate-400 mb-2">Moyennes Mobiles</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm"><span className="text-slate-300">SMA 20</span><span className="font-mono text-white">${algoAnalysis.indicators?.sma_20 || "—"}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-300">SMA 50</span><span className="font-mono text-white">${algoAnalysis.indicators?.sma_50 || "—"}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-300">SMA 200</span><span className="font-mono text-white">${algoAnalysis.indicators?.sma_200 || "—"}</span></div>
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 sm:p-4">
                  <div className="text-xs text-slate-400 mb-2">Bandes de Bollinger</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm"><span className="text-slate-300">Haute</span><span className="font-mono text-red-400">${algoAnalysis.indicators?.bb_upper || "—"}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-300">Milieu</span><span className="font-mono text-white">${algoAnalysis.indicators?.bb_middle || "—"}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-300">Basse</span><span className="font-mono text-emerald-400">${algoAnalysis.indicators?.bb_lower || "—"}</span></div>
                  </div>
                </div>
              </div>

              {/* Trade Setup */}
              <div className="gradient-green rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Meilleur Setup d'Entrée</div>
                  {algoAnalysis.trade_setup?.entry_discount > 0 && <span className="px-2 py-0.5 bg-cyan-500/15 text-cyan-400 text-[10px] font-bold rounded">-{algoAnalysis.trade_setup.entry_discount}% vs prix actuel</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div><div className="text-slate-300 text-xs mb-1">Entrée Optimale</div><div className="font-mono text-base sm:text-lg font-bold text-white">{curr}{algoAnalysis.trade_setup?.entry || "—"}</div></div>
                  <div><div className="text-slate-300 text-xs mb-1">Stop-Loss</div><div className="font-mono text-base sm:text-lg font-bold text-red-400">{curr}{algoAnalysis.trade_setup?.stop_loss || "—"}</div></div>
                  <div><div className="text-slate-300 text-xs mb-1">Target</div><div className="font-mono text-base sm:text-lg font-bold text-emerald-400">{curr}{algoAnalysis.trade_setup?.target || "—"}</div></div>
                  <div><div className="text-slate-300 text-xs mb-1">Risk/Reward</div><div className="font-mono text-base sm:text-lg font-bold text-blue-400">{algoAnalysis.trade_setup?.risk_reward || "—"}:1</div></div>
                </div>
                {/* Budget Recommendation */}
                {(() => {
                  const sizing = calcPositionSize(portfolioValue, algoAnalysis);
                  if (!sizing) return null;
                  return (
                    <div className="mt-3 pt-3 border-t border-emerald-500/20" data-testid="budget-recommendation">
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-3.5 h-3.5 text-amber-400" />
                        <span className="text-xs text-amber-400 font-bold uppercase tracking-wider">Budget Recommandé</span>
                        <span className="text-[9px] text-slate-500 ml-auto">Risque {sizing.riskPctUsed}% | {sizing.capCategory}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3">
                        <div className="bg-slate-800/60 rounded-lg p-2.5 text-center">
                          <div className="text-[10px] text-slate-400 mb-0.5">Investir</div>
                          <div className="font-mono text-lg font-black text-amber-300" data-testid="recommended-amount">{sizing.amount.toLocaleString()}€</div>
                          <div className="text-[9px] text-slate-500">{sizing.pctPortfolio}% du portefeuille</div>
                        </div>
                        <div className="bg-slate-800/60 rounded-lg p-2.5 text-center">
                          <div className="text-[10px] text-slate-400 mb-0.5">Gain Potentiel</div>
                          <div className="font-mono text-lg font-bold text-emerald-400" data-testid="potential-gain">+{sizing.potentialGain.toLocaleString()}€</div>
                          <div className="text-[9px] text-slate-500">si target atteint</div>
                        </div>
                        <div className="bg-slate-800/60 rounded-lg p-2.5 text-center">
                          <div className="text-[10px] text-slate-400 mb-0.5">Perte Max</div>
                          <div className="font-mono text-lg font-bold text-red-400" data-testid="potential-loss">-{sizing.potentialLoss.toLocaleString()}€</div>
                          <div className="text-[9px] text-slate-500">si stop touché</div>
                        </div>
                      </div>
                      {sizing.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {sizing.tags.map((tag, i) => (
                            <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-300 border border-slate-600/40">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {algoAnalysis.trade_setup?.entry_reason && (
                  <div className="mt-2 text-xs text-slate-400 border-t border-slate-700 pt-2">
                    <span className="text-emerald-400 font-semibold">Raison:</span> {algoAnalysis.trade_setup.entry_reason}
                    {algoAnalysis.trade_setup?.current_price && algoAnalysis.trade_setup.entry < algoAnalysis.trade_setup.current_price && <span className="ml-2 text-slate-500">(Prix actuel: {curr}{algoAnalysis.trade_setup.current_price})</span>}
                  </div>
                )}
              </div>

              {/* Valuation / Fair Value Panel */}
              {valuationData[algoAnalysis.ticker] && (() => {
                const v = valuationData[algoAnalysis.ticker];
                const c = v.coefficient;
                return (
                  <div className="mt-4 glass-card rounded-lg p-4" data-testid="algo-valuation-panel">
                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">Valorisation — Fair Value</div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center"><div className="text-xs text-slate-400 mb-1">Fair Value</div><div className="font-mono text-2xl font-bold text-white">{curr}{formatNumber(v.fair_value)}</div></div>
                      <div className="text-center"><div className="text-xs text-slate-400 mb-1">Coefficient</div><div className={`font-mono text-2xl font-bold ${c <= 0.85 ? "text-emerald-400" : c <= 1.1 ? "text-blue-400" : "text-red-400"}`}>{c}x</div></div>
                      <div className="text-center"><div className="text-xs text-slate-400 mb-1">Verdict</div><div className={`font-bold text-lg ${c <= 0.85 ? "text-emerald-400" : c <= 1.1 ? "text-blue-400" : "text-red-400"}`}>{c <= 0.85 ? "SOUS-EVALUÉ" : c <= 1.1 ? "JUSTE PRIX" : "SUR-EVALUÉ"}</div></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[{ label: "P/E Trailing", val: v.trailing_pe }, { label: "P/E Forward", val: v.forward_pe }, { label: "Price/Book", val: v.pb }, { label: "EV/EBITDA", val: v.ev_ebitda },
                        { label: "Marge Nette", val: v.profit_margin ? `${v.profit_margin}%` : null }, { label: "Marge Brute", val: v.gross_margin ? `${v.gross_margin}%` : null },
                        { label: "ROE", val: v.roe ? `${v.roe}%` : null }, { label: "Croiss. CA", val: v.revenue_growth ? `${v.revenue_growth}%` : null },
                      ].map((m, i) => (<div key={i} className="bg-slate-700/30 rounded p-2"><div className="text-[10px] text-slate-500">{m.label}</div><div className="font-mono text-sm font-bold text-white">{m.val || "—"}</div></div>))}
                    </div>
                    {v.target_mean && (
                      <div className="mt-4 flex items-center gap-4 bg-slate-700/30 rounded-lg p-3">
                        <div className="text-xs text-slate-400">Analystes ({v.num_analysts})</div>
                        <div className="flex-1 flex items-center gap-2">
                          <span className="font-mono text-xs text-red-400">{curr}{v.target_low}</span>
                          <div className="flex-1 h-2 bg-slate-600 rounded-full relative">
                            <div className="absolute h-full bg-gradient-to-r from-red-500 via-blue-500 to-emerald-500 rounded-full" style={{ width: '100%' }} />
                            <div className="absolute w-3 h-3 bg-white rounded-full top-1/2 -translate-y-1/2 border-2 border-blue-400"
                              style={{ left: `${Math.min(Math.max(((algoAnalysis.price - v.target_low) / (v.target_high - v.target_low)) * 100, 0), 100)}%` }} />
                          </div>
                          <span className="font-mono text-xs text-emerald-400">{curr}{v.target_high}</span>
                        </div>
                        <div className="font-mono text-sm font-bold text-blue-400">Cible: {curr}{v.target_mean}</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Send to AI Committee button */}
              <div className="mt-6 flex justify-center gap-4">
                <button data-testid="send-to-committee-btn" onClick={onSendToCommittee}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-lg flex items-center gap-3 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl">
                  <Users className="w-5 h-5" />Demander Zones d'Entrée/Sortie au Comité<ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ); })()}

          {/* Backtest Section */}
          <div className="glass-card rounded-xl p-3 sm:p-5">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
              <h3 className="font-display text-base sm:text-lg font-bold flex items-center gap-2"><History className="w-5 h-5 text-emerald-400" />Backtest</h3>
              <div className="flex gap-2">
                <select value={backtestStrategy} onChange={(e) => setBacktestStrategy(e.target.value)}
                  className="px-3 sm:px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-xs sm:text-sm text-white">
                  <option value="RSI_MEAN_REVERSION">RSI Mean Reversion</option>
                  <option value="TREND_FOLLOWING">Trend Following</option>
                </select>
                <button onClick={runBacktest} disabled={algoLoading || !algoTicker}
                  className="px-3 sm:px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg text-xs sm:text-sm flex items-center gap-2">
                  <Play className="w-4 h-4" />Run
                </button>
              </div>
            </div>
            {backtestResult && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center"><div className="text-xs text-slate-400 mb-1">Return</div><div className={`font-mono text-2xl font-bold ${backtestResult.total_return_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{backtestResult.total_return_pct >= 0 ? "+" : ""}{backtestResult.total_return_pct}%</div></div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center"><div className="text-xs text-slate-400 mb-1">Win Rate</div><div className="font-mono text-2xl font-bold text-white">{backtestResult.win_rate_pct}%</div></div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center"><div className="text-xs text-slate-400 mb-1">Max Drawdown</div><div className="font-mono text-2xl font-bold text-red-400">-{backtestResult.max_drawdown_pct}%</div></div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center"><div className="text-xs text-slate-400 mb-1">Total Trades</div><div className="font-mono text-2xl font-bold text-white">{backtestResult.total_trades}</div></div>
              </div>
            )}
            {backtestResult && (
              <div data-testid="backtest-conclusion-section">
                {!backtestConclusion ? (
                  <button onClick={onGetBacktestConclusion}
                    className="w-full py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-semibold hover:from-cyan-500/30 hover:to-blue-500/30 transition-all flex items-center justify-center gap-2"
                    data-testid="backtest-conclusion-btn">
                    <Cpu className="w-4 h-4" /> Conclusion AI — Opportunité ou pas ?
                  </button>
                ) : backtestConclusion === "loading" ? (
                  <div className="w-full py-4 bg-slate-800/50 rounded-lg text-center"><RefreshCw className="w-5 h-5 text-cyan-400 animate-spin mx-auto mb-2" /><div className="text-sm text-slate-400">Analyse en cours...</div></div>
                ) : backtestConclusion.format === "structured" ? (() => {
                  const c = backtestConclusion.conclusion;
                  const verdictConfig = {
                    "ACHETER": { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400", icon: TrendingUp },
                    "ATTENDRE": { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400", icon: Clock },
                    "EVITER": { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400", icon: ShieldAlert },
                  };
                  const vc = verdictConfig[c.verdict] || verdictConfig["ATTENDRE"];
                  const VIcon = vc.icon;
                  return (
                    <div className={`rounded-xl ${vc.bg} border ${vc.border} overflow-hidden`} data-testid="backtest-conclusion-card">
                      <div className="px-5 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${vc.bg} border ${vc.border} flex items-center justify-center`}><VIcon className={`w-5 h-5 ${vc.text}`} /></div>
                          <div><div className={`text-xl font-black tracking-wide ${vc.text}`}>{c.verdict}</div><div className="text-xs text-slate-400">{c.resume}</div></div>
                        </div>
                        <div className="text-right"><div className="text-[10px] text-slate-500 uppercase">Confiance</div><div className={`text-2xl font-mono font-black ${vc.text}`}>{c.confiance}<span className="text-sm text-slate-500">/10</span></div></div>
                      </div>
                      <div className="grid grid-cols-2 gap-0 border-t border-slate-700/50">
                        <div className="p-4 border-r border-slate-700/50">
                          <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-2">Points forts</div>
                          {(c.forces || []).map((f, i) => (<div key={i} className="flex items-start gap-2 mb-1.5"><ChevronUp className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="text-xs text-slate-300">{f}</span></div>))}
                        </div>
                        <div className="p-4">
                          <div className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-2">Faiblesses</div>
                          {(c.faiblesses || []).map((f, i) => (<div key={i} className="flex items-start gap-2 mb-1.5"><ChevronDown className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" /><span className="text-xs text-slate-300">{f}</span></div>))}
                        </div>
                      </div>
                      <div className="border-t border-slate-700/50 px-5 py-3"><div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Contexte sectoriel</div><div className="text-xs text-slate-300">{c.contexte}</div></div>
                      <div className={`px-5 py-3 border-t border-slate-700/50 ${vc.bg}`}><div className={`text-xs font-bold ${vc.text} flex items-center gap-2`}><Target className="w-3.5 h-3.5" /> {c.action}</div></div>
                    </div>
                  );
                })() : (
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2"><Cpu className="w-4 h-4 text-cyan-400" /><span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Conclusion AI</span></div>
                    <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{typeof backtestConclusion === "object" ? backtestConclusion.conclusion : backtestConclusion}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: History + Portfolio Optimization */}
        <div className="lg:col-span-4 space-y-4 sm:space-y-6">
          {/* Algo History */}
          <div className="glass-card rounded-xl p-3 sm:p-5" data-testid="algo-history-panel">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" />Historique</h3>
              <span className="text-xs text-slate-500">{algoHistory.length} analyses</span>
            </div>
            {algoHistory.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {algoHistory.map((h, i) => {
                  const a = h.analysis;
                  const isActive = algoAnalysis?.ticker === a?.ticker;
                  const timeAgo = h.timestamp ? (() => { const mins = Math.round((Date.now() - new Date(h.timestamp).getTime()) / 60000); if (mins < 60) return `${mins}min`; if (mins < 1440) return `${Math.round(mins / 60)}h`; return `${Math.round(mins / 1440)}j`; })() : "";
                  return (
                    <div key={i} onClick={() => { setAlgoTicker(h.ticker); setAlgoAnalysis(a); }}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${isActive ? "bg-cyan-500/10 border border-cyan-500/25" : "bg-slate-800/50 hover:bg-slate-700/50"}`}
                      data-testid={`algo-history-${h.ticker}`}>
                      <div><div className="font-mono text-sm font-bold text-white">{h.ticker}</div><div className="text-[10px] text-slate-500">{timeAgo}</div></div>
                      <div className="text-right">
                        <div className="font-mono text-xs text-slate-300">{getTickerCurrency(h.ticker)}{a?.price}</div>
                        <span className={`text-[10px] font-bold ${a?.analysis?.signal === "BUY" ? "text-emerald-400" : a?.analysis?.signal === "SELL" ? "text-red-400" : "text-amber-400"}`}>{a?.analysis?.signal} ({a?.analysis?.score})</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-center py-6 text-slate-500 text-sm">Aucune analyse sauvegardée</div>}
          </div>

          {/* Top 5 Convictions */}
          <div className="glass-card rounded-xl p-3 sm:p-5" data-testid="top-convictions-panel">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-base sm:text-lg font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" />Top 5 Convictions</h3>
            </div>
            {topRated && topRated.length > 0 ? (
              <div className="space-y-2.5">
                {topRated.map((stock, i) => {
                  const curr = stock.currency ? ({"EUR":"€","GBP":"£","CHF":"CHF ","SEK":"kr ","NOK":"kr ","DKK":"kr ","HKD":"HK$","JPY":"¥","USD":"$"}[stock.currency] || "$") : getTickerCurrency(stock.ticker);
                  const signalColors = { "STRONG_BUY": "text-emerald-400 bg-emerald-500/15 border-emerald-500/25", "BUY": "text-green-400 bg-green-500/10 border-green-500/20", "HOLD": "text-amber-400 bg-amber-500/10 border-amber-500/20", "SELL": "text-red-400 bg-red-500/10 border-red-500/20" };
                  const medals = ["text-amber-400", "text-slate-300", "text-orange-500", "text-slate-400", "text-slate-500"];
                  const sparkline = stock.sparkline || [];
                  const sparkMin = sparkline.length > 0 ? Math.min(...sparkline) : 0;
                  const sparkMax = sparkline.length > 0 ? Math.max(...sparkline) : 1;
                  const sparkRange = sparkMax - sparkMin || 1;
                  const momentum1m = sparkline.length >= 2 ? ((sparkline[sparkline.length - 1] - sparkline[0]) / sparkline[0] * 100) : null;
                  const fd = stock.fund_details;
                  const tradeTypeStyles = { "LT": "text-cyan-400 bg-cyan-500/15 border-cyan-500/25", "SWING": "text-amber-400 bg-amber-500/15 border-amber-500/25", "SWING_COURT": "text-orange-400 bg-orange-500/15 border-orange-500/25", "SCALP": "text-rose-400 bg-rose-500/15 border-rose-500/25", "WATCH": "text-slate-400 bg-slate-500/10 border-slate-500/20" };
                  const tradeTypeLabels = { "LT": "LONG TERM", "SWING": "SWING", "SWING_COURT": "SW COURT", "SCALP": "SCALP", "WATCH": "WATCH" };
                  return (
                    <div key={stock.ticker} onClick={() => { setAlgoTicker(stock.ticker); runAlgoAnalysis(stock.ticker); }}
                      className="p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 cursor-pointer transition-all border border-transparent hover:border-cyan-500/20"
                      data-testid={`top-conviction-${stock.ticker}`}>
                      {/* Row 1: Rank + Ticker + Signal + Price */}
                      <div className="flex items-center gap-2.5">
                        <div className={`text-xl font-black w-6 text-center ${medals[i] || "text-slate-500"}`}>{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-white">{stock.ticker}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${signalColors[stock.tech_signal] || "text-slate-400 bg-slate-700 border-slate-600"}`}>
                              {stock.tech_signal?.replace("_", " ")}
                            </span>
                            {stock.trade_type && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tradeTypeStyles[stock.trade_type] || ""}`}>
                                {tradeTypeLabels[stock.trade_type]}
                              </span>
                            )}
                            {stock.conviction && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${stock.conviction === "HIGH" ? "bg-emerald-500/10 text-emerald-400" : stock.conviction === "MEDIUM" ? "bg-amber-500/10 text-amber-400" : "bg-slate-700 text-slate-400"}`}>
                                {stock.conviction === "HIGH" ? "80%+" : stock.conviction === "MEDIUM" ? "60%" : "40%"}
                              </span>
                            )}
                            {stock.sector && <span className="text-[9px] text-slate-500 hidden sm:inline">{stock.sector}</span>}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">{stock.name}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-sm font-bold text-white">{curr}{formatNumber(stock.price)}</div>
                          {stock.target && stock.price > 0 && (
                            <div className={`text-[9px] font-mono ${stock.target > stock.price ? "text-emerald-500" : "text-red-400"}`}>
                              {stock.target > stock.price ? "+" : ""}{((stock.target - stock.price) / stock.price * 100).toFixed(0)}% ({curr}{stock.target})
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Row 2: Sparkline + Scores */}
                      <div className="flex items-center gap-2.5 mt-2 pl-8">
                        {/* Mini Sparkline */}
                        {sparkline.length > 2 ? (
                          <div className="flex-1">
                            <svg viewBox={`0 0 ${sparkline.length - 1} 20`} className="w-full h-5" preserveAspectRatio="none">
                              <defs>
                                <linearGradient id={`sg-${stock.ticker}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0" stopColor={momentum1m >= 0 ? "#34d399" : "#f87171"} stopOpacity="0.3" />
                                  <stop offset="1" stopColor={momentum1m >= 0 ? "#34d399" : "#f87171"} stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <path d={`M0,${20 - (sparkline[0] - sparkMin) / sparkRange * 20} ${sparkline.map((v, j) => `L${j},${20 - (v - sparkMin) / sparkRange * 20}`).join(" ")} L${sparkline.length - 1},20 L0,20 Z`} fill={`url(#sg-${stock.ticker})`} />
                              <path d={`M0,${20 - (sparkline[0] - sparkMin) / sparkRange * 20} ${sparkline.map((v, j) => `L${j},${20 - (v - sparkMin) / sparkRange * 20}`).join(" ")}`} fill="none" stroke={momentum1m >= 0 ? "#34d399" : "#f87171"} strokeWidth="1.5" />
                            </svg>
                            {momentum1m !== null && (
                              <div className={`text-[9px] font-mono font-bold ${momentum1m >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                {momentum1m >= 0 ? "+" : ""}{momentum1m.toFixed(1)}% (1M)
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex-1 h-5 bg-slate-700/30 rounded" />
                        )}
                        {/* Score badges */}
                        <div className="flex flex-col gap-1 shrink-0 items-end">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] text-slate-500">Tech</span>
                            <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded ${stock.tech_score >= 70 ? "bg-emerald-500/15 text-emerald-400" : stock.tech_score >= 50 ? "bg-amber-500/10 text-amber-400" : "bg-red-500/10 text-red-400"}`}>
                              {stock.tech_score}
                            </span>
                          </div>
                          {fd && fd.score > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] text-slate-500">Fund</span>
                              <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded ${fd.score >= 70 ? "bg-cyan-500/15 text-cyan-400" : fd.score >= 50 ? "bg-amber-500/10 text-amber-400" : "bg-slate-700 text-slate-400"}`}>
                                {fd.score} <span className="text-[8px] opacity-60">({fd.active_signals}/{fd.total_signals})</span>
                              </span>
                            </div>
                          ) : stock.fund_score > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] text-slate-500">Fund</span>
                              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">{stock.fund_score}</span>
                            </div>
                          ) : null}
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400" />
                            <span className="text-[10px] font-bold text-amber-400">{stock.composite}</span>
                          </div>
                          {stock.recommended_investment > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-slate-500">Invest</span>
                              <span className="text-[10px] font-mono font-bold text-emerald-400">{stock.recommended_investment}€</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 text-sm">
                Analysez des actions pour voir vos meilleures convictions ici
              </div>
            )}
          </div>

          {/* Portfolio Recommendations (compact) */}
          {portfolioOptimization?.recommendations?.length > 0 && (
            <div className="glass-card rounded-xl p-3 sm:p-5" data-testid="algo-recommendations">
              <h3 className="font-display text-sm font-bold flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-orange-400" />Recommandations Portfolio</h3>
              <div className="space-y-1.5">
                {portfolioOptimization.recommendations.slice(0, 5).map((rec, i) => (
                  <div key={i} className={`p-2 rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-80 ${rec.type === "CUT_LOSS" || rec.type === "REDUCE" ? "bg-red-500/10 border border-red-500/20" : rec.type === "TAKE_PROFIT" ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-yellow-500/10 border border-yellow-500/20"}`}
                    onClick={() => { setAlgoTicker(rec.ticker); runAlgoAnalysis(rec.ticker); }}>
                    {rec.type === "CUT_LOSS" || rec.type === "REDUCE" ? <X className="w-3 h-3 text-red-400 shrink-0" /> : rec.type === "TAKE_PROFIT" ? <Check className="w-3 h-3 text-emerald-400 shrink-0" /> : <Minus className="w-3 h-3 text-yellow-400 shrink-0" />}
                    <span className="text-xs font-bold text-white">{rec.ticker}</span>
                    <span className="text-[10px] text-slate-400 uppercase">{rec.type.replace("_", " ")}</span>
                    <span className="text-[10px] text-slate-500 ml-auto truncate max-w-[120px]">{rec.reason?.split(' ').slice(0, 5).join(' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Tickers */}
          <div className="glass-card rounded-xl p-5">
            <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-3">Quick Analyze</div>
            <div className="flex flex-wrap gap-2">
              {["NVDA", "AAPL", "TSLA", "PLTR", "MSTR", "ASTS"].map(ticker => (
                <button key={ticker} onClick={() => { setAlgoTicker(ticker); runAlgoAnalysis(ticker); }}
                  className="px-3 py-1.5 bg-slate-700 text-slate-300 border border-slate-600 rounded-lg text-xs font-mono hover:text-white hover:border-emerald-500/30 transition-colors">
                  {ticker}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
